// Modern location tracking component using Expo Location
// Handles GPS permissions and location updates across all platforms

import { useEffect, useRef, useState } from 'react';
import { Text, TouchableOpacity, View } from 'react-native';
import AppConstants from '../AppConstants.js';
import { useAPI } from '../contexts/APIContext.js';
import { useGeo } from '../contexts/GeoContext.js';
import { useJourney } from '../contexts/JourneyContext.js';

export default function GeoTracking(props) {
  const [errorMsg, setErrorMsg] = useState(null);
  const [debugLocationLog, setDebugLocationLog] = useState([]);
  const location = props.location || null;
  const checkinIntervalRef = useRef(null);

  const geoContext = useGeo();
  const journey = useJourney();
  const api = useAPI();

  const startingDistance = journey.startingDistance;

  // Fallback mechanism: if startingDistance is null/undefined and we have valid GPS coordinates and a location,
  // set the starting distance to the current distance as a fallback
  useEffect(() => {
    if (!startingDistance && 
        location && 
        geoContext.coordinates?.latitude && 
        geoContext.coordinates?.longitude) {
      const currentDistance = geoContext.distanceToLocation(location);
      if (currentDistance !== null && currentDistance > 0) {
        console.log(`Setting fallback starting distance: ${currentDistance.toFixed(2)}m`);
        journey.setStartingDistance(currentDistance);
      }
    }
  }, [startingDistance, location, geoContext.coordinates?.latitude, geoContext.coordinates?.longitude, geoContext, journey]);

  // Calculate warmth level from 0-10 based on distance to target and starting distance
  // Returns -10 if way off, 0 if no data, 1-10 for warmth level

  const _warmthLevel = () => {
    const warmthSections = 10;

    let warmthLevel = 0;
    const distance = geoContext.distanceToLocation(location);
    // console.log("Distance to target:", distance);
    if (distance === null) return 0;

    const areWeClose = distance < location.radius;

    if (journey.startingDistance && distance > 0) {
      
      if (areWeClose === true) {
        warmthLevel = 10;
      } else {
        let stepSize = journey.startingDistance / warmthSections; 
        let steps = Math.round(distance / stepSize) + 1;
        warmthLevel = (warmthSections + 1) - steps;
        //console.log("Warmth: " + warmthLevel + " from " + distance + " of " + journey.startingDistance);
      }

      if (warmthLevel < -1) {
        warmthLevel = -10; // way off
      } else {
        if (warmthLevel === 10 && !areWeClose) {
          warmthLevel = 9;
        }
      }
    }
    // console.log("Final warmth level:", warmthLevel);
    return warmthLevel;
  }

  // Store the latest geoContext and api references to avoid stale closures
  const geoContextRef = useRef(geoContext);
  const apiRef = useRef(api);

  // Debug: track location updates in state for display
  useEffect(() => {
    if (journey.isDebuggingGPS && geoContext.coordinates?.latitude && geoContext.coordinates?.longitude) {
      setDebugLocationLog(log => [
        `${new Date().toLocaleTimeString()}: ${geoContext.coordinates.latitude.toFixed(6)}, ${geoContext.coordinates.longitude.toFixed(6)}`,
        ...log.slice(0, 19)
      ]);
    }
  }, [geoContext.coordinates?.latitude, geoContext.coordinates?.longitude, journey.isDebuggingGPS]);

  // Set up recurring checkin when component mounts and location tracking is active
  useEffect(() => {
    // Only start checkin if we have a location to track
    if (location && journey.voyagerCode) {
      //console.log(`Starting recurring checkin every ${AppConstants.checkinInterval}ms for location ${location.id}`);
      
      const performCheckin = async () => {
        try {
          // Use refs to get the latest context values, avoiding stale closures
          const coords = geoContextRef.current.coordinates;
          if (coords && coords.latitude !== null && coords.longitude !== null) {
            await apiRef.current.checkin(
              journey.voyagerCode,
              coords.latitude,
              coords.longitude,
              location,
              "GPS tracking update",
              null
            );
            console.log("Checkin completed with coords:", coords.latitude.toFixed(6), coords.longitude.toFixed(6));
          } else {
            console.log("Skipping checkin - no valid coordinates available");
          }
        } catch (error) {
          console.error("Checkin failed:", error);
          setErrorMsg("Checkin failed");
        }
      };

      // Perform initial checkin
      performCheckin();

      // Set up recurring checkin interval
      checkinIntervalRef.current = setInterval(performCheckin, AppConstants.checkinInterval);

      // Cleanup interval on unmount or when dependencies change
      return () => {
        if (checkinIntervalRef.current) {
          //console.log("Stopping recurring checkin");
          clearInterval(checkinIntervalRef.current);
          checkinIntervalRef.current = null;
        }
      };
    }
    // Note: Intentionally excluding geoContext.coordinates, api, and location object 
    // from dependencies to prevent interval restart on every GPS update
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.id, journey.voyagerCode]);

  // Return an array of characters representing warmth (★ / ☆) or ! fallback
  const warmthLevelChars = () => {
    let warmth = _warmthLevel();
    const warmthSections = 10;
    let stars = [];

    const distance = geoContext.distanceToLocation(location);
    // console.log("Distance to target:", distance);
    if (distance === null) return []; // Return empty array, not 0

    const areWeClose = distance < location.radius;

    if (warmth > 0) {
      if (warmth < -1) {
        for (let i = 0; i < warmthSections; i++) stars.push('!');
      } else {
        if (warmth === 10 && !areWeClose) warmth = 9; // guard against rounding overshoot
        for (let i = 0; i < warmthSections; i++) {
          stars.push(i < warmth ? '★' : '☆');
        }
      }
    } else {
      for (let i = 0; i < warmthSections; i++) stars.push('☆');
    }
    //console.log("Warmth chars:", stars);
    return stars; // empty array until we have enough data
  }

  return (
    <>
      {journey.isDebuggingGPS && (
        <View style={{height: 180, alignItems: 'center', justifyContent: 'center', backgroundColor: '#ccc'}}>
          <View style={{flex: 1, marginTop: 10}}>
            <Text>Distance: {geoContext.distanceToLocation(location)?.toFixed(2) || 'N/A'}m</Text>
            <Text>Starting Distance: {startingDistance?.toFixed(2) || 'N/A'}m</Text>
            <Text>Target Radius: {location?.radius?.toFixed(2) || 'N/A'}m</Text>
            <Text>Within Range: {geoContext.isWithinLocationRange(location) ? 'Yes' : 'No'}</Text>
            <Text>Coords: {geoContext.coordinates?.latitude?.toFixed(2) || 'N/A'} x {geoContext.coordinates?.longitude?.toFixed(2) || 'N/A'}</Text>
            <Text>Heading: {geoContext.heading?.toFixed(2) || 'N/A'}°</Text>
            {errorMsg && <Text style={{color: 'red'}}>Error: {errorMsg}</Text>}
            <Text style={{marginTop: 10, fontWeight: 'bold'}}>Recent Location Updates:</Text>
            {debugLocationLog.map((entry, idx) => (
              <Text key={idx} style={{fontSize: 12, color: '#333'}}>{entry}</Text>
            ))}
          </View>
        </View>
      )}
      
      {/* Check if GPS permissions are missing */}
      {!geoContext.hasGPSAccess || !(geoContext.coordinates?.latitude) || !(geoContext.coordinates?.longitude) ? (
        <TouchableOpacity
          onPress={journey.goToPermissions}
          style={{
            paddingTop: 20,
            paddingBottom: 20,
            paddingLeft: 25,
            paddingRight: 25,
            backgroundColor: '#d4574a',
            opacity: 0.9,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Text
            style={{
              fontSize: 16,
              color: '#fff',
              fontWeight: 'bold',
              textAlign: 'center',
            }}
          >
            No GPS. Tap to confirm permissions.
          </Text>
        </TouchableOpacity>
      ) : (
        <View style={{
          paddingTop: 20,
          paddingBottom: 20,
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingLeft: 25,
          paddingRight: 25,
          paddingHorizontal: 5,
          backgroundColor: '#777',
          opacity: 0.75,
        }}>
          {warmthLevelChars().map((c, idx) => (
            <Text
              key={idx}
              style={{
                flex: 1,
                textAlign: 'center',
                fontSize: 22,
                lineHeight: 22,              
                color: c === '★' ? '#ffb400' : '#eee',
                textShadowColor: c === '★' ? 'rgba(255,180,0,0.6)' : 'transparent',
                textShadowOffset: { width: 0, height: 0 },
                textShadowRadius: c === '★' ? 3 : 0
              }}
            >
              {c}
            </Text>
          ))}
        </View>
      )}
    </>
  );
}

